import { keepPreviousData, queryOptions } from '@tanstack/react-query'

import { api, ensureData, toFormData } from '@/shared/api/client'
import { compactQuery } from '@/shared/api/list'
import type { components } from '@/shared/api/schema'
import type { Option } from '@/shared/components/option'
import { i18n } from '@/shared/i18n'

export type Lookup = components['schemas']['LookupResource']
export type LookupSummary = components['schemas']['LookupSummaryResource']
export type AppModule = components['schemas']['AppModuleResource']
export type SaveAppModuleInput = components['schemas']['SaveAppModuleRequest']

export const LOOKUP_KINDS = ['positions', 'regions', 'departments', 'levels', 'channels'] as const
export type LookupKind = (typeof LOOKUP_KINDS)[number]

export function isLookupKind(value: string): value is LookupKind {
  return (LOOKUP_KINDS as readonly string[]).includes(value)
}

/** Regiões e níveis têm imagem; níveis são ordenáveis e têm cor/bônus; canais podem ser solicitados no app. */
export const lookupFeatures: Record<LookupKind, { image: boolean; level: boolean; channel: boolean }> = {
  positions: { image: false, level: false, channel: false },
  regions: { image: true, level: false, channel: false },
  departments: { image: false, level: false, channel: false },
  levels: { image: true, level: true, channel: false },
  channels: { image: false, level: false, channel: true },
}

export interface SaveLookupInput {
  name: string
  code?: string | null
  is_active?: boolean
  color?: string | null
  bonus?: number | null
  is_requestable?: boolean
}

export interface LookupListParams {
  page?: number | undefined
  sort?: string | undefined
  search?: string | undefined
}

/*
 * Os cinco cadastros têm o mesmo contrato (LookupResource); o caminho é
 * fixado em "/positions" só para o tipo do openapi-fetch.
 */
type ListPath = '/positions'
type ItemPath = '/positions/{position}'

export const lookupKeys = {
  all: (kind: LookupKind) => ['lookups', kind] as const,
  list: (kind: LookupKind, params: LookupListParams) => ['lookups', kind, 'list', params] as const,
  options: (kind: LookupKind) => ['lookups', kind, 'options'] as const,
}

export function lookupListQuery(kind: LookupKind, params: LookupListParams) {
  return queryOptions({
    queryKey: lookupKeys.list(kind, params),
    queryFn: async () =>
      ensureData(
        await api.GET(`/${kind}` as ListPath, {
          params: {
            query: compactQuery({
              page: params.page,
              sort: params.sort as never,
              'filter[search]': params.search,
            }),
          },
        }),
      ),
    placeholderData: keepPreviousData,
  })
}

async function fetchAllLookups(kind: LookupKind): Promise<Lookup[]> {
  return ensureData(
    await api.GET(`/${kind}` as ListPath, {
      params: { query: { per_page: 500, sort: kind === 'levels' ? 'sort_order' : 'name' } },
    }),
  ).data
}

/** Todos os itens do cadastro (até 500) para selects e filtros. */
export function lookupOptionsQuery(kind: LookupKind) {
  return queryOptions({
    queryKey: lookupKeys.options(kind),
    queryFn: () => fetchAllLookups(kind),
    staleTime: 5 * 60 * 1000,
    select: (items): Option[] =>
      items.map((item) => ({
        value: item.id,
        label: item.is_active ? item.name : i18n.t('segmentation.inactiveOption', { name: item.name }),
      })),
  })
}

function itemPath(kind: LookupKind, id: string) {
  return { path: `/${kind}/{id}` as ItemPath, params: { path: { position: id, id } as never } }
}

export async function saveLookup(kind: LookupKind, input: SaveLookupInput, id?: string): Promise<Lookup> {
  if (id) {
    const { path, params } = itemPath(kind, id)
    return ensureData(await api.PUT(path, { params, body: input })).data
  }

  return ensureData(await api.POST(`/${kind}` as ListPath, { body: input })).data
}

export async function deleteLookup(kind: LookupKind, id: string): Promise<void> {
  const { path, params } = itemPath(kind, id)
  await api.DELETE(path, { params })
}

export async function uploadLookupImage(kind: 'regions' | 'levels', id: string, file: File): Promise<Lookup> {
  return ensureData(
    await api.POST(`/${kind}/{id}/image` as '/regions/{region}/image', {
      params: { path: { region: id, id } as never },
      body: {} as never,
      bodySerializer: () => toFormData({ image: file }),
    }),
  ).data
}

export async function deleteLookupImage(kind: 'regions' | 'levels', id: string): Promise<void> {
  await api.DELETE(`/${kind}/{id}/image` as '/regions/{region}/image', {
    params: { path: { region: id, id } as never },
  })
}

export async function reorderLevels(ids: string[]): Promise<void> {
  await api.PUT('/levels/order', { body: { ids } })
}

// Módulos do app

const all = ['app-modules'] as const

export const appModuleKeys = {
  all,
  list: (page: number) => [...all, 'list', page] as const,
}

export function appModuleListQuery(page: number) {
  return queryOptions({
    queryKey: appModuleKeys.list(page),
    queryFn: async () => ensureData(await api.GET('/app-modules', { params: { query: { page } } })),
    placeholderData: keepPreviousData,
  })
}

export async function saveAppModule(input: SaveAppModuleInput, id?: string): Promise<AppModule> {
  if (id) {
    return ensureData(
      await api.PUT('/app-modules/{appModule}', { params: { path: { appModule: id } }, body: input }),
    ).data
  }

  return ensureData(await api.POST('/app-modules', { body: input })).data
}

export async function deleteAppModule(id: string): Promise<void> {
  await api.DELETE('/app-modules/{appModule}', { params: { path: { appModule: id } } })
}

/** Como `lookupOptionsQuery`, mas o valor é o código (configurações referenciam cadastros por código). */
export function lookupCodeOptionsQuery(kind: LookupKind) {
  return queryOptions({
    queryKey: [...lookupKeys.options(kind), 'by-code'] as const,
    queryFn: () => fetchAllLookups(kind),
    staleTime: 5 * 60 * 1000,
    select: (items): Option[] => items.map((item) => ({ value: item.code, label: item.name })),
  })
}
